/**
 * Cliente HTTP central do Eye.
 *
 * Responsabilidades: prefixo `/api`, injeção do token Bearer, serialização
 * JSON, tratamento uniforme de erros (`{"error": "..."}`) e, no 401, limpeza da
 * sessão + emissão do evento global que redireciona ao login.
 */

import { clearSession, getToken, notifyUnauthorized } from "@/lib/auth";

const BASE_URL = "/api";

/** Erro de API com o status HTTP e a mensagem amigável do backend. */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

interface RequestOptions {
  method?: string;
  /** Corpo a ser serializado como JSON. */
  body?: unknown;
  signal?: AbortSignal;
  /** Envia o header Authorization (padrão: true). Login usa `false`. */
  auth?: boolean;
}

/** Extrai a mensagem de erro do corpo `{"error": "..."}`, com fallback. */
async function extractError(res: Response): Promise<string> {
  try {
    const data: unknown = await res.json();
    if (
      data &&
      typeof data === "object" &&
      "error" in data &&
      typeof (data as { error: unknown }).error === "string"
    ) {
      return (data as { error: string }).error;
    }
  } catch {
    // Corpo vazio ou não-JSON — usa o fallback abaixo.
  }
  return `Falha na requisição (HTTP ${res.status}).`;
}

/** Executa a requisição, aplicando auth e o tratamento de 401. */
async function doFetch(path: string, options: RequestOptions): Promise<Response> {
  const { method = "GET", body, signal, auth = true } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (auth) {
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  if (res.status === 401) {
    clearSession();
    notifyUnauthorized();
    throw new ApiError("Sessão expirada. Faça login novamente.", 401);
  }

  return res;
}

/** Requisição que devolve JSON tipado. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await doFetch(path, options);
  if (!res.ok) {
    throw new ApiError(await extractError(res), res.status);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/** Requisição que devolve um Blob (ex.: download de CSV). */
export async function apiRequestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const res = await doFetch(path, options);
  if (!res.ok) {
    throw new ApiError(await extractError(res), res.status);
  }
  return res.blob();
}
